<template>
  <n-layout style="height: 100vh;" position="absolute">
    <!-- 顶部导航栏 - 固定不滚动 -->
    <n-layout-header bordered position="absolute" style="height: 64px; padding: 0 24px; display: flex; align-items: center; z-index: 1000;">
      <div style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
        <!-- 面包屑导航 -->
        <n-breadcrumb>
          <n-breadcrumb-item @click="goHome" style="cursor: pointer;">
            <n-icon size="18" style="vertical-align: -0.15em; margin-right: 4px;">
              <Home />
            </n-icon>
            首页
          </n-breadcrumb-item>
          <n-breadcrumb-item v-for="item in breadcrumbItems" :key="item.path" @click="navigateTo(item.path)" style="cursor: pointer;">
            <n-icon v-if="item.icon" size="18" style="vertical-align: -0.15em; margin-right: 4px;">
              <component :is="item.icon" />
            </n-icon>
            {{ item.name }}
          </n-breadcrumb-item>
        </n-breadcrumb>
        
        <!-- 右侧操作区 -->
        <n-space>
          <!-- 主题切换按钮 -->
          <n-button circle quaternary @click="toggleTheme">
            <template #icon>
              <n-icon size="20">
                <Sunny v-if="!isDark" />
                <Moon v-else />
              </n-icon>
            </template>
          </n-button>
        </n-space>
      </div>
    </n-layout-header>
    
    <!-- 主内容区域 - 添加顶部边距以避免被固定导航栏遮挡 -->
    <n-layout-content position="absolute" style="top: 64px; padding: 24px; overflow-y: auto;">
      <!-- 路由视图：子页面在这里渲染 -->
      <router-view />
    </n-layout-content>
  </n-layout>
</template>

<script lang="ts">
import { defineComponent, inject, Ref, computed, Component } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { Home, Sunny, Moon } from '@vicons/ionicons5';
import { Api } from '@vicons/tabler';
import { Wallet, TextFont } from '@vicons/carbon';

interface BreadcrumbItem {
  name: string;
  path: string;
  icon?: Component;
}

export default defineComponent({
  name: 'MainLayout',
  components: {
    Home,
    Sunny,
    Moon
  },
  setup() {
    const router = useRouter();
    const route = useRoute();
    
    // 从 App.vue 注入主题状态和切换方法
    const isDark = inject<Ref<boolean>>('isDark');
    const toggleTheme = inject<() => void>('toggleTheme');
    
    // 路由图标映射
    const routeIconMap: Record<string, Component> = {
      'EasyApis': Api,
      'EasyApisHome': Api,
      'JiZhang': Wallet,
      'JiZhangHome': Wallet,
      'YuLin': Wallet,
      'ShenMu': Wallet,
      'TextProcess': TextFont,
      'TextProcessHome': TextFont,
      'TextReplace': TextFont
    };
    
    // 面包屑名称映射配置
    const routeNameMap: Record<string, string> = {
      'EasyApis': 'EasyApis',
      'EasyApisHome': 'EasyApis',
      'JiZhang': '记账',
      'JiZhangHome': '记账',
      'YuLin': '榆林记账',
      'ShenMu': '神木记账',
      'TextProcess': '文本处理',
      'TextProcessHome': '文本处理',
      'TextReplace': '替换文本'
    };
    
    // 计算面包屑导航项
    const breadcrumbItems = computed<BreadcrumbItem[]>(() => {
      const items: BreadcrumbItem[] = [];
      const pathArray = route.path.split('/').filter(p => p);
      
      if (pathArray.length === 0) {
        return items;
      }
      
      // 构建面包屑路径
      let currentPath = '';
      pathArray.forEach((segment) => {
        currentPath += '/' + segment;
        
        // 根据路径段获取名称和图标
        let name = segment;
        let icon: Component | undefined = undefined;
        
        // 尝试从路由名称获取
        const matchedRoute = router.resolve(currentPath).matched[router.resolve(currentPath).matched.length - 1];
        if (matchedRoute && matchedRoute.name) {
          const routeName = matchedRoute.name as string;
          name = routeNameMap[routeName] || (matchedRoute.meta?.title as string) || segment;
          icon = routeIconMap[routeName];
        }
        
        items.push({
          name,
          path: currentPath,
          icon
        });
      });
      
      return items;
    });
    
    // 返回首页
    const goHome = () => {
      router.push('/');
    };
    
    // 导航到指定路径
    const navigateTo = (path: string) => {
      router.push(path);
    };
    
    return {
      isDark,
      toggleTheme,
      breadcrumbItems,
      goHome,
      navigateTo
    };
  }
});
</script>

<style scoped>
/* 主布局样式 */
</style>
